import type { AccumulatedNode, AccumulatedEdge } from '../types';
import { classifyIOC, type IOCType } from './iocClassifier';

export interface SerializeOptions {
  maxNodes?: number;       // default 200, truncate with "[N more...]"
  includeEdges?: boolean;  // default true
  title?: string;          // optional heading
  maxChars?: number;       // default ~16000 (~4000 tokens)
}

const DEFAULT_MAX_NODES = 200;
const DEFAULT_MAX_CHARS = 16000;

/**
 * Convert a filtered graph into structured markdown suitable for LLM prompt injection.
 * Nodes are grouped by label, IOCs sub-grouped by type. Edges listed as key relationships.
 */
export function serializeGraphToPrompt(
  nodes: AccumulatedNode[],
  edges: AccumulatedEdge[],
  options?: SerializeOptions,
): string {
  const maxNodes = options?.maxNodes ?? DEFAULT_MAX_NODES;
  const includeEdges = options?.includeEdges ?? true;
  const maxChars = options?.maxChars ?? DEFAULT_MAX_CHARS;
  const title = options?.title ?? 'Graph Investigation';

  // Prioritize seed/high-score nodes when truncating
  const sorted = [...nodes].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  const truncated = sorted.length > maxNodes;
  const selected = truncated ? sorted.slice(0, maxNodes) : sorted;
  const selectedIds = new Set(selected.map(n => n.id));

  // Group nodes by label
  const groups = new Map<string, AccumulatedNode[]>();
  for (const n of selected) {
    const list = groups.get(n.label) || [];
    list.push(n);
    groups.set(n.label, list);
  }

  const lines: string[] = [];
  lines.push(`## Graph Context: ${title}`);
  lines.push(`${nodes.length} nodes, ${edges.length} edges`);
  if (truncated) {
    lines.push(`(showing top ${maxNodes} of ${nodes.length} nodes)`);
  }
  lines.push('');

  // Label display order
  const labelOrder = ['ThreatActor', 'MalwareFamily', 'IOC', 'AttackTechnique', 'CVE', 'Document', 'WebSource'];
  const labelNames: Record<string, string> = {
    ThreatActor: 'Threat Actors',
    MalwareFamily: 'Malware Families',
    IOC: 'IOCs',
    AttackTechnique: 'ATT&CK Techniques',
    CVE: 'CVEs',
    Document: 'Documents',
    WebSource: 'Web Sources',
  };

  for (const label of labelOrder) {
    const group = groups.get(label);
    if (!group || group.length === 0) continue;

    const section = labelNames[label] || label;

    if (label === 'IOC') {
      lines.push(`### ${section} (${group.length})`);
      // Sub-group by IOC type
      const byType = new Map<IOCType, string[]>();
      for (const n of group) {
        const t = classifyIOC(n.name);
        const list = byType.get(t) || [];
        list.push(n.name);
        byType.set(t, list);
      }
      const typeOrder: IOCType[] = ['IP', 'DOMAIN', 'HASH', 'URL', 'EMAIL', 'UNKNOWN'];
      const typeLabels: Record<IOCType, string> = {
        IP: 'IPs', DOMAIN: 'Domains', HASH: 'Hashes',
        URL: 'URLs', EMAIL: 'Emails', UNKNOWN: 'Other',
      };
      for (const t of typeOrder) {
        const vals = byType.get(t);
        if (!vals || vals.length === 0) continue;
        const truncateAt = 15;
        const display = vals.length > truncateAt
          ? vals.slice(0, truncateAt).join(', ') + `, ... [${vals.length - truncateAt} more]`
          : vals.join(', ');
        lines.push(`**${typeLabels[t]} (${vals.length}):** ${display}`);
      }
    } else if (label === 'AttackTechnique') {
      lines.push(`### ${section} (${group.length})`);
      for (const n of group) {
        const attackId = n.properties?.attack_id as string | undefined;
        const prefix = attackId ? `${attackId} ` : '';
        lines.push(`- ${prefix}${n.name}`);
      }
    } else if (label === 'ThreatActor') {
      lines.push(`### ${section} (${group.length})`);
      for (const n of group) {
        const country = n.properties?.country as string | undefined;
        const suffix = country ? ` -- country: ${country}` : '';
        lines.push(`- **${n.name}**${suffix}`);
      }
    } else if (label === 'MalwareFamily') {
      lines.push(`### ${section} (${group.length})`);
      if (group.length <= 10) {
        for (const n of group) {
          const platform = n.properties?.platform as string | undefined;
          const suffix = platform ? ` -- platform: ${platform}` : '';
          lines.push(`- **${n.name}**${suffix}`);
        }
      } else {
        // Compact format for many families
        lines.push(group.map(n => `**${n.name}**`).join(', '));
      }
    } else if (label === 'Document') {
      lines.push(`### ${section} (${group.length})`);
      const docLimit = 20;
      const docs = group.slice(0, docLimit);
      for (const n of docs) {
        const date = n.properties?.date as string | undefined;
        const org = n.properties?.organization as string | undefined;
        const titleProp = n.properties?.title as string | undefined;
        const displayTitle = titleProp && titleProp.length > 60
          ? titleProp.slice(0, 60) + '...'
          : titleProp || n.name;
        const meta = [date, org].filter(Boolean).join(', ');
        lines.push(`- ${displayTitle}${meta ? ` (${meta})` : ''}`);
      }
      if (group.length > docLimit) {
        lines.push(`- ... [${group.length - docLimit} more documents]`);
      }
    } else if (label === 'CVE') {
      lines.push(`### ${section} (${group.length})`);
      for (const n of group) {
        const cvss = n.properties?.cvss_score as number | undefined;
        const severity = n.properties?.cvss_severity as string | undefined;
        const suffix = cvss != null ? ` -- CVSS ${cvss}${severity ? ` (${severity})` : ''}` : '';
        lines.push(`- **${n.name}**${suffix}`);
      }
    } else {
      lines.push(`### ${section} (${group.length})`);
      for (const n of group) {
        lines.push(`- ${n.name}`);
      }
    }
    lines.push('');
  }

  // Key relationships
  if (includeEdges && edges.length > 0) {
    const relevantEdges = edges.filter(e => selectedIds.has(e.source) && selectedIds.has(e.target));
    if (relevantEdges.length > 0) {
      lines.push(`### Key Relationships (${relevantEdges.length})`);
      // Build id→name map
      const nameMap = new Map<string, string>();
      for (const n of selected) nameMap.set(n.id, n.name);

      const edgeLimit = 50;
      const display = relevantEdges.slice(0, edgeLimit);
      for (const e of display) {
        const src = nameMap.get(e.source) || e.source;
        const tgt = nameMap.get(e.target) || e.target;
        lines.push(`- ${src} --${e.rel_type}--> ${tgt}`);
      }
      if (relevantEdges.length > edgeLimit) {
        lines.push(`- ... [${relevantEdges.length - edgeLimit} more relationships]`);
      }
      lines.push('');
    }
  }

  let result = lines.join('\n');

  // Enforce character budget
  if (result.length > maxChars) {
    result = result.slice(0, maxChars - 50) + '\n\n... [truncated to fit token budget]';
  }

  return result;
}
